import {useCommonItems} from "../../../../../contexts/CommonItensProvider";
import {useEffect, useState} from "react";
import {
    carregaConfigsEquipamento,
    carregaEquipamentosAll,
    carregaHistoricos,
    carregaLockers,
    duplicarEquipamento
} from "../configs/functions";
import {
    Box, Button,
    Chip, CircularProgress,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle, Divider,
    FormControl,
    InputLabel,
    MenuItem, Radio,
    Select,
    Stack, Typography
} from "@mui/material";
import {CopyAll, CopyAllOutlined, Edit, Person, Restore, Settings} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import HistoryIcon from "@mui/icons-material/History";
import {blue, green, grey} from "@mui/material/colors";
import {motoresToNormalized, portasToNormalized, snapshotToNormalized} from "../configs/layoutAdapters";
import {EquipmentLayoutPreview} from "./EquipmentLayoutPreview";

const ORIGEM_META = {
    store: {label: 'Criação', color: 'success', Icon: Settings},
    update: {label: 'Edição', color: 'info', Icon: Edit},
    copia: {label: 'Cópia', color: 'warning', Icon: CopyAllOutlined},
    restauracao: {label: 'Restauração', color: 'default', Icon: Restore},
};

export function CopyEquipmentSettingsDialog({
                                                open,
                                                onClose,
                                                onSuccess,
                                                tipo,
                                                idEmpresa,
                                                equipamento,
                                                dadosCriacao
                                            }) {
    const { exibirAlerta } = useCommonItems();

    const isEdicao = !!equipamento?.id;
    const isArmario = tipo === 'armario';

    const [fonte, setFonte] = useState('equipamento');

    const [loading, setLoading] = useState(false);
    const [loadingHist, setLoadingHist] = useState(false);
    const [confirmando, setConfirmando] = useState(false);

    const [equipamentos, setEquipamentos] = useState([]);
    const [historicos, setHistoricos] = useState([]);

    const [equipamentoOrigem, setEquipamentoOrigem] = useState('');
    const [historicoSelecionado, setHistoricoSelecionado] = useState(null);

    const [layoutAntes, setLayoutAntes] = useState(null);
    const [layoutDepois, setLayoutDepois] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const normalizar = (item) => ({
        id: item.id,
        nome: item.nome,
        tag: item.tag_identificacao ?? item.tagIdentificacao ?? '',
        tipo: item.tipo ?? 'armario',
        status: item.status,
    });

    const loadEquipamentos = async () => {
        setLoading(true);

        try {
            const { data } = await carregaEquipamentosAll({
                id_empresas: [idEmpresa]
            });

            const brutos = isArmario
                ? (data?.armarios?.data ?? [])
                : (data?.equipamentos?.data ?? []);

            const lista = brutos
                .map(normalizar)
                .filter(e => isArmario || e.tipo === tipo)
                .filter(e => e.status === 1)
                .sort((a, b) => a.nome.localeCompare(b.nome));

            setEquipamentos(lista);
        } catch (error) {
            exibirAlerta(
                'Erro ao carregar equipamentos',
                error?.response?.data?.error || '',
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    const loadHistoricos = async (idEquipamento) => {
        if (!idEquipamento) {
            setHistoricos([]);
            return;
        }

        setLoadingHist(true);

        try {
            const { data } = await carregaHistoricos({
                id_empresas: [idEmpresa],
                id_equipamento: idEquipamento,
                tipo_equipamento: tipo,
                detalhado: 1,
                limit: 10
            });

            setHistoricos(data?.data ?? []);
        } catch (error) {
            exibirAlerta(
                'Erro ao carregar históricos',
                error?.response?.data?.error || '',
                'error'
            );
        } finally {
            setLoadingHist(false);
        }
    };

    useEffect(() => {
        if (open && idEmpresa) {
            loadEquipamentos();
        }
        if (!open) {
            setEquipamentoOrigem('');
            setHistoricoSelecionado(null);
            setHistoricos([]);
            setEquipamentos([]);
            setFonte('equipamento');
        }
    }, [open, idEmpresa, tipo]);

    useEffect(() => {
        if (fonte === 'historico' && equipamentoOrigem) {
            loadHistoricos(equipamentoOrigem);
        }
    }, [fonte, equipamentoOrigem]);

    const podeConfirmar = fonte === 'equipamento'
        ? !!equipamentoOrigem
        : !!historicoSelecionado;

    const handleConfirmar = async () => {
        setConfirmando(true);

        try {
            const payload = {
                tipo_equipamento: tipo,
                id_origem: fonte === 'equipamento'
                    ? equipamentoOrigem
                    : historicoSelecionado.idEquipamento,
                ...(fonte === 'historico' && { id_historico: historicoSelecionado.id }),
                ...(isEdicao
                        ? { id_destino: equipamento.id }
                        : {
                            id_empresa: idEmpresa,
                            nome: dadosCriacao?.nome,
                            tag_identificacao: dadosCriacao?.tag_identificacao || undefined,
                            id_ponto_entrega: dadosCriacao?.id_ponto_entrega,
                            descricao: dadosCriacao?.descricao,
                        }
                ),
            };

            const { data } = await duplicarEquipamento(payload);

            exibirAlerta(
                isEdicao ? 'Configurações copiadas com sucesso' : 'Equipamento duplicado com sucesso',
                '',
                'success'
            );

            onSuccess?.(data?.data);
            onClose();
        } catch (error) {
            exibirAlerta(
                'Erro ao copiar configurações',
                error?.response?.data?.error
                || Object.values(error?.response?.data?.errors || {}).flat().join(' ')
                || '',
                'error'
            );
        } finally {
            setConfirmando(false);
        }
    };

    const labelTipo = {
        maquina: 'Máquina',
        balcao: 'Balcão',
        virtual: 'Balcão Virtual',
        armario: 'Armário',
    }[tipo] ?? 'Equipamento';

    const equipamentoOrigemObj = equipamentos.find(e => e.id === equipamentoOrigem);

    const equipamentosVisiveis = fonte === 'equipamento'
        ? equipamentos.filter(e => e.id !== equipamento?.id)
        : equipamentos;

    const equipamentosOrdenados = fonte === 'historico' && isEdicao
        ? [
            ...equipamentosVisiveis.filter(e => e.id === equipamento.id),
            ...equipamentosVisiveis.filter(e => e.id !== equipamento.id),
        ]
        : equipamentosVisiveis;

    useEffect(() => {
        if (fonte === 'historico' && isEdicao && !equipamentoOrigem) {
            setEquipamentoOrigem(equipamento.id);
        }

        if (fonte === 'equipamento' && equipamentoOrigem === equipamento?.id) {
            setEquipamentoOrigem('');
        }
    }, [fonte]);

    const colsGrid = isArmario
        ? (equipamento?.numeroColunas ?? equipamento?.numero_colunas ?? 5)
        : (dadosCriacao?.qtd_motores === 60 || equipamento?.qtd_motores === 60 ? 10 : 6);

    const carregarLayoutDeId = async (id) => {
        if (isArmario) {
            const { data } = await carregaLockers({ id, locker_doors: true });
            const portasApi = data?.data?.[0]?.portas ?? [];

            const portas = portasApi.map((p) => ({
                numero_logico: p.numeroLogico,
                configuracoes: [{ id_produto: p.configuracoes?.[0]?.produto?.id ?? null }],
                linha_inicial: p.linhaInicial ?? 0,
                coluna_inicial: p.colunaInicial ?? 0,
                numero_linhas: p.numeroLinhas ?? 1,
                numero_colunas: p.numeroColunas ?? 1,
            }));

            return portasToNormalized(portas, colsGrid);
        }

        const { data } = await carregaConfigsEquipamento({ id_equipamento: id });
        const motores = data?.data ?? [];
        const cols = motores.length > 36 ? 10 : 6;
        return motoresToNormalized(motores, cols);
    };

    useEffect(() => {
        let ativo = true;

        const montar = async () => {
            const temSelecao = fonte === 'equipamento' ? equipamentoOrigem : historicoSelecionado;
            if (!temSelecao) {
                setLayoutAntes(null);
                setLayoutDepois(null);
                return;
            }

            setLoadingPreview(true);
            try {
                const antes = isEdicao ? await carregarLayoutDeId(equipamento.id) : null;

                let depois;
                if (fonte === 'historico' && historicoSelecionado) {
                    depois = snapshotToNormalized(
                        historicoSelecionado.configuracoes,
                        isArmario ? 'armario' : 'maquina',
                        colsGrid
                    );
                } else {
                    depois = await carregarLayoutDeId(equipamentoOrigem);
                }

                if (ativo) {
                    setLayoutAntes(antes);
                    setLayoutDepois(depois);
                }
            } catch {
                if (ativo) exibirAlerta('Erro ao gerar pré-visualização', '', 'error');
            } finally {
                if (ativo) setLoadingPreview(false);
            }
        };

        montar();
        return () => { ativo = false; };
    }, [fonte, equipamentoOrigem, historicoSelecionado, isEdicao]);


    return (
        <Dialog open={open} onClose={onClose} maxWidth={isEdicao ? "md" : "sm"} fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CopyAll fontSize="small" />

                Copiar configurações
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {isEdicao
                        ? `As configurações atuais desta ${labelTipo.toLowerCase()} serão SUBSTITUÍDAS. Nome, código e dados de conexão não mudam.`
                        : `As configurações serão copiadas para a nova ${labelTipo.toLowerCase()}. As quantidades de produto começam zeradas.`
                    }
                </Alert>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                        icon={<CopyAllOutlined />}
                        label="Configuração atual"
                        onClick={() => { setFonte('equipamento'); setHistoricoSelecionado(null); }}
                        color={fonte === 'equipamento' ? 'primary' : 'default'}
                        variant={fonte === 'equipamento' ? 'filled' : 'outlined'}
                    />

                    <Chip
                        icon={<HistoryIcon />}
                        label="Versão anterior"
                        onClick={() => setFonte('historico')}
                        color={fonte === 'historico' ? 'primary' : 'default'}
                        variant={fonte === 'historico' ? 'filled' : 'outlined'}
                    />
                </Stack>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{labelTipo} de origem</InputLabel>

                    <Select
                        value={equipamentoOrigem}
                        label={`${labelTipo} de origem`}
                        onChange={(e) => {
                            setEquipamentoOrigem(e.target.value);
                            setHistoricoSelecionado(null);
                        }}
                        disabled={loading}
                        variant="outlined"
                    >
                        {loading && (
                            <MenuItem disabled>
                                <CircularProgress size={16} sx={{ mr: 1 }} /> Carregando...
                            </MenuItem>
                        )}

                        {!loading && equipamentosOrdenados.length === 0 && (
                            <MenuItem disabled>
                                Nenhuma {labelTipo.toLowerCase()} disponível nesta empresa
                            </MenuItem>
                        )}

                        {equipamentosOrdenados.map((eq) => {
                            const ehPropria = eq.id === equipamento?.id;

                            return (
                                <MenuItem key={eq.id} value={eq.id}>
                                    {eq.nome}

                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: grey[600] }}>
                                        {eq.tag}
                                    </Typography>

                                    {ehPropria && (
                                        <Chip
                                            label="Atual"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ ml: 1, height: 18, fontSize: 11 }}
                                        />
                                    )}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                {fonte === 'historico' && equipamentoOrigem && (
                    <>
                        <Divider sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                Versões de <strong>{equipamentoOrigemObj?.nome}</strong>
                            </Typography>
                        </Divider>

                        {loadingHist ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : historicos.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                Nenhum histórico registrado para este equipamento.
                            </Typography>
                        ) : (
                            <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                                {historicos.map((h) => {
                                    const meta = ORIGEM_META[h.origem] ?? { label: h.origem, color: 'default', Icon: HistoryIcon };
                                    const selecionado = historicoSelecionado?.id === h.id;
                                    const { Icon } = meta;

                                    return (
                                        <Box
                                            key={h.id}
                                            onClick={() => setHistoricoSelecionado(h)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                p: 1.5,
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: selecionado ? 'primary.main' : grey[300],
                                                backgroundColor: selecionado ? green[50] : 'transparent',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    borderColor: selecionado ? 'primary.main' : grey[400],
                                                    backgroundColor: selecionado ? green[50] : grey[50],
                                                },
                                            }}
                                        >
                                            <Radio
                                                checked={selecionado}
                                                size="small"
                                                tabIndex={-1}
                                                sx={{ p: 0 }}
                                            />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '50%',
                                                    flexShrink: 0,
                                                    backgroundColor: selecionado ? 'primary.main' : grey[100],
                                                    color: selecionado ? '#fff' : grey[600],
                                                }}
                                            >
                                                <Icon fontSize="small" />
                                            </Box>

                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                    {h.data}
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ mt: 0.25, color: 'text.secondary' }}
                                                >
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Settings sx={{ fontSize: 13 }} />
                                                        <Typography variant="caption">
                                                            {h.qtdConfigs ?? 0} {tipo === 'armario'
                                                                ? (h.qtdConfigs === 1 ? 'porta' : 'portas')
                                                                : (h.qtdConfigs === 1 ? 'motor' : 'motores')
                                                            }
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </>
                )}

                {(layoutDepois || loadingPreview) && (
                    <>
                        <Divider sx={{ my: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                Pré-visualização
                            </Typography>
                        </Divider>

                        {loadingPreview ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Stack
                                direction={isEdicao ? { xs: 'column', md: 'row' } : 'column'}
                                spacing={2}
                                alignItems="center"
                                justifyContent="center"
                            >
                                {isEdicao && layoutAntes && (
                                    <>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <EquipmentLayoutPreview
                                                layout={layoutAntes}
                                                titulo="Antes (atual)"
                                                compacto
                                            />
                                        </Box>

                                        <Box sx={{ color: grey[400], fontSize: 28, px: 1 }}>→</Box>
                                    </>
                                )}

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <EquipmentLayoutPreview
                                        layout={layoutDepois}
                                        titulo={isEdicao ? 'Depois (será aplicado)' : 'Será criado com'}
                                        compacto
                                    />
                                </Box>
                            </Stack>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={confirmando}>
                    Cancelar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleConfirmar}
                    disabled={!podeConfirmar || confirmando}
                    startIcon={confirmando ? <CircularProgress size={16} /> : <CopyAll />}
                >
                    {confirmando ? 'Copiando...' : 'Copiar configurações'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}